import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3-force';
import { ForceGraphConfig, ForceGraphData, ForceNode, ForceLink } from './config/forceGraphTypes';
import { useForceSimulation } from './hooks/useForceSimulation';

interface ForceGraphCanvasProps {
    data: ForceGraphData;
    config: ForceGraphConfig;
    width: number;
    height: number;
    onNodeClick?: (node: ForceNode) => void;
}

export const ForceGraphCanvas: React.FC<ForceGraphCanvasProps> = ({
    data,
    config,
    width,
    height,
    onNodeClick
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [hoveredNode, setHoveredNode] = useState<ForceNode | null>(null);
    const [transform, setTransform] = useState({ k: 1, x: 0, y: 0 });

    // Render Function
    const render = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, width, height);
        ctx.save();
        ctx.translate(transform.x, transform.y);
        ctx.scale(transform.k, transform.k);

        // Draw Links
        data.links.forEach(link => {
            const source = link.source as ForceNode;
            const target = link.target as ForceNode;
            if (typeof source !== 'object' || typeof target !== 'object') return;

            ctx.beginPath();
            ctx.moveTo(source.x || 0, source.y || 0);
            ctx.lineTo(target.x || 0, target.y || 0);

            const isConnected = hoveredNode && (link.source === hoveredNode || link.target === hoveredNode);
            const isDimmed = hoveredNode && !isConnected;

            ctx.strokeStyle = isConnected ? '#f59e0b' : (config.linkColorMode === 'single' ? config.linkSingleColor : '#94a3b8');
            ctx.lineWidth = isConnected ? 2 : 1;
            ctx.globalAlpha = isDimmed ? 0.1 : config.linkOpacity;
            ctx.stroke();

            // Draw Arrows
            if (config.showArrows) {
                const angle = Math.atan2((target.y || 0) - (source.y || 0), (target.x || 0) - (source.x || 0));
                const targetRadius = config.minNodeRadius + 2; // Offset slightly more than radius
                const arrowLength = 8;

                // Offset from target center
                const arrowX = (target.x || 0) - targetRadius * Math.cos(angle);
                const arrowY = (target.y || 0) - targetRadius * Math.sin(angle);

                ctx.beginPath();
                ctx.moveTo(arrowX, arrowY);
                ctx.lineTo(arrowX - arrowLength * Math.cos(angle - Math.PI / 6), arrowY - arrowLength * Math.sin(angle - Math.PI / 6));
                ctx.lineTo(arrowX - arrowLength * Math.cos(angle + Math.PI / 6), arrowY - arrowLength * Math.sin(angle + Math.PI / 6));
                ctx.closePath();
                ctx.fillStyle = ctx.strokeStyle;
                ctx.fill();
            }

            ctx.globalAlpha = 1;
        });

        // Draw Nodes
        data.nodes.forEach(node => {
            const isHovered = hoveredNode === node;
            const isDimmed = hoveredNode && hoveredNode !== node && !data.links.some(l => (l.source === hoveredNode && l.target === node) || (l.target === hoveredNode && l.source === node));

            ctx.beginPath();
            const radius = isHovered ? (config.minNodeRadius + 2) : config.minNodeRadius; // Simplified radius logic
            ctx.arc(node.x || 0, node.y || 0, radius, 0, 2 * Math.PI);

            ctx.fillStyle = config.nodeColorMode === 'single' ? config.nodeSingleColor : (node.color || '#4f46e5');
            ctx.globalAlpha = isDimmed ? 0.2 : config.nodeOpacity;
            ctx.fill();

            if (isHovered || node.id === hoveredNode?.id) {
                ctx.strokeStyle = '#f59e0b';
                ctx.lineWidth = 2;
                ctx.stroke();
            }
            ctx.globalAlpha = 1;

            // Labels
            if (config.showNodeLabels && (!isDimmed || isHovered)) {
                ctx.fillStyle = '#fff';
                ctx.font = '10px sans-serif';
                ctx.fillText(node.label || node.id, (node.x || 0) + radius + 2, (node.y || 0) + 3);
            }
        });

        ctx.restore();
    }, [data, config, width, height, transform, hoveredNode]);

    // Simulation
    const simulation = useForceSimulation(data, config, width, height, render);

    // Interaction Handlers (Simplified for brevity, would need d3-drag or custom event handling)
    // For now, basic mouse move for hover
    const isDragging = useRef(false);
    const lastPos = useRef({ x: 0, y: 0 });

    const handleMouseDown = (e: React.MouseEvent) => {
        isDragging.current = true;
        lastPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
        isDragging.current = false;
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging.current) {
            const dx = e.clientX - lastPos.current.x;
            const dy = e.clientY - lastPos.current.y;
            setTransform(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
            lastPos.current = { x: e.clientX, y: e.clientY };
            render();
        } else {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const mouseX = (e.clientX - rect.left - transform.x) / transform.k;
            const mouseY = (e.clientY - rect.top - transform.y) / transform.k;

            // Find node under mouse
            const node = simulation?.simulation.find(mouseX, mouseY, 20);
            setHoveredNode(node || null);
            render(); // Re-render on hover change
        }
    };

    const handleClick = (e: React.MouseEvent) => {
        if (hoveredNode && onNodeClick) {
            onNodeClick(hoveredNode);
        }
    };

    const handleWheel = (e: React.WheelEvent) => {
        const zoomIntensity = 0.1;
        const delta = e.deltaY > 0 ? -zoomIntensity : zoomIntensity;
        const newScale = Math.max(0.1, Math.min(8, transform.k + delta));

        setTransform(prev => ({ ...prev, k: newScale }));
        render();
    };

    return (
        <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className="w-full h-full cursor-move"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
            onClick={handleClick}
        />
    );
};
